import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { buildContactQuery } from './useContact';
import { invalidateAllContactFilterQueries } from './useContactFilterQuery';

interface UpdateContactRequest {
	contactId: string;
	contactData: Partial<Api.IContact>;
	allowArchived?: boolean;
	expandHouseholdMembers?: boolean;
	allowDuplicateEmail?: boolean;
	expand?: string;
}

export const useContactUpdateMutation = ({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IContact, variables: UpdateContactRequest) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) => {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ contactId, contactData, expand, allowDuplicateEmail }: UpdateContactRequest) => {
			// Match the original ContactViewModel.update() API call with proper query parameters
			const queryParams = {
				expand: expand || 'HouseholdMembers',
				allowDuplicateEmail: allowDuplicateEmail || true,
			};

			return userSession.webServiceHelper.callAsync<Api.IContact>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `contact/${contactId}`,
					queryParams,
				}),
				'PUT',
				contactData
			);
		},
		onSuccess: (data, variables) => {
			// Invalidate and refetch contact queries - use the same key structure
			const contactQueryKey = buildContactQuery({
				contactId: variables.contactId,
				allowArchived: variables.allowArchived || false,
				expandHouseholdMembers: variables.expandHouseholdMembers,
			});
			queryClient.invalidateQueries({ queryKey: contactQueryKey });
			invalidateAllContactFilterQueries();

			onSuccess?.(data, variables);
		},
		onError,
	});
};
